// (Copyright) Confluent, Inc.

import React, { type ReactNode } from 'react';
import styled from 'styled-components';
import { useTooltipTriggerState } from 'react-stately';
import { VisuallyHidden } from '@reach/visually-hidden';
import { mergeProps, useTooltip, useTooltipTrigger, useOverlayPosition } from 'react-aria';

import { Portal } from '../Portal';

export type PropTypes = {
  accessibilityLabel?: string;
  children: ReactNode;
  content: ReactNode;
  display?: 'inline' | 'inline-block' | 'flex' | 'inline-flex' | 'block';
  enterDelayMs?: number;
  inherit?: boolean;
  isPresentationOnly?: boolean;
  isTooltipDisabled?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  preferredPlacement?: 'top' | 'left' | 'bottom' | 'right';
  trigger?: 'hover' | 'focus-hover';
  width?: string;
};

const UnstyledButton = styled.button`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  display: flex;
  cursor: pointer;
`;

/* eslint-disable rulesdir/no-css-var-colors */
export const TooltipDisplay = styled.div`
  padding: 10px 12px;
  max-width: 201px;
  z-index: var(--POPOVER_INSIDE_MODAL_ZINDEX);
  font-size: 14px;
  line-height: 22px;
  background-color: var(--DIM_DARK);
  color: var(--WHITE);
  border-radius: 4px;
  box-shadow: var(--SHADOW_HOVER);
  width: max-content;
  overflow-wrap: break-word;
`;
/* eslint-enable rulesdir/no-css-var-colors */

const TooltipPopup = ({ isPresentationOnly, state, placement, targetRef, ...props }) => {
  const overlayRef = React.useRef();
  const { tooltipProps } = useTooltip(props, state);
  const { overlayProps } = useOverlayPosition({
    targetRef,
    overlayRef,
    placement,
    containerPadding: 0,
    offset: 8,
    isOpen: state.isOpen,
  });

  return (
    <Portal>
      <TooltipDisplay
        aria-live={isPresentationOnly ? 'off' : 'assertive'}
        ref={overlayRef}
        {...(isPresentationOnly ? { role: 'presentation' } : {})}
        {...mergeProps(props, tooltipProps, overlayProps)}
      />
    </Portal>
  );
};

const TooltipButton = React.forwardRef<HTMLElement, PropTypes>(
  (
    {
      accessibilityLabel,
      children,
      content,
      display = 'inline-block',
      enterDelayMs = 400,
      inherit = false,
      onOpenChange,
      trigger = 'focus-hover',
      preferredPlacement = 'top',
      isTooltipDisabled = false,
      isPresentationOnly = false,
      width = 'auto',
    }: PropTypes,
    propRef
  ) => {
    const reactAriaProps = {
      delay: enterDelayMs,
      isDisabled: isTooltipDisabled,
      onOpenChange,
    };
    const state = useTooltipTriggerState(reactAriaProps);
    const innerRef = React.useRef(null);
    const ref = propRef || innerRef;
    // @ts-expect-error - TS2345 - Argument of type 'ForwardedRef<HTMLElement>' is not assignable to parameter of type 'RefObject<FocusableElement>'.
    const { triggerProps, tooltipProps } = useTooltipTrigger(reactAriaProps, state, ref);
    React.useEffect(() => {
      const handleUserKeypress = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          state.close(true);
        }
      };

      if (state.isOpen) {
        window.addEventListener('keydown', handleUserKeypress);
      } else {
        window.removeEventListener('keydown', handleUserKeypress);
      }

      return () => {
        window.removeEventListener('keydown', handleUserKeypress);
      };
    }, [state]);
    const innerChild = React.useMemo(() => {
      const buttonMethods = {
        onFocus: () => {
          if (trigger === 'focus-hover') {
            state.open();
          }
        },
        onBlur: () => {
          if (trigger === 'focus-hover') {
            state.close();
          }
        },
      };

      if (propRef) {
        const child = React.Children.only(children);

        // @ts-expect-error - TS2769 - No overload matches this call. | TS2339 - Property 'props' does not exist on type 'boolean | {} | ReactChild | ReactPortal'.
        return React.cloneElement(children, mergeProps(triggerProps, buttonMethods, child.props));
      } else if (inherit) {
        const child = React.Children.only(children);
        // had to break this out as inlining it caused prettier to break the flow ignore
        // @ts-expect-error - TS2339 - Property 'props' does not exist on type 'boolean | {} | ReactChild | ReactPortal'.
        const props = mergeProps(ref, triggerProps, buttonMethods, child.props);

        // @ts-expect-error - TS2769 - No overload matches this call.
        return React.cloneElement(children, props);
      }

      return (
        <UnstyledButton
          ref={ref}
          {...triggerProps}
          {...buttonMethods}
          css={{ width }}
          type="button"
        >
          {children}
        </UnstyledButton>
      );
    }, [propRef, inherit, ref, triggerProps, width, children, trigger, state]);

    return (
      <div style={{ position: 'relative', display, width }}>
        {innerChild}
        {state.isOpen && (
          <TooltipPopup
            isPresentationOnly={isPresentationOnly}
            placement={preferredPlacement}
            state={state}
            targetRef={ref}
            {...tooltipProps}
          >
            {accessibilityLabel ? (
              <>
                <VisuallyHidden>{accessibilityLabel}</VisuallyHidden>
                <span aria-hidden={true}>{content}</span>
              </>
            ) : (
              content
            )}
          </TooltipPopup>
        )}
      </div>
    );
  }
);

TooltipButton.displayName = 'Tooltip';

export default TooltipButton;
